/**
 * `canary workflow fork <workflowId>` and
 * `canary workflow fork-map <forkId> <originalId>`.
 *
 * Forking creates an editable draft copy of a workflow that links back to its
 * origin (`forkedFromWorkflowId`). The fork's nodes are NEW ids but the node
 * graph + configJson are copied faithfully, so playbooks (per-node cache content)
 * are portable back to the origin once proven on the fork — but only if you can
 * map each fork node back to its origin counterpart, which is exactly what
 * `fork-map` prints.
 *
 * Output mode auto-detects: JSON when piped (`!process.stdout.isTTY`), pretty
 * text when running interactively. `--json` and `--text` flags force either
 * mode.
 */

import process from 'node:process';
import { hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';
import { topoOrderNodes } from '@chatsdet/workflow-diff';
import type { WorkflowDetailResponse } from './workflow-format.js';
import type { LiveWorkflow } from '@chatsdet/canary-spec';

// ── Types ────────────────────────────────────────────────────────────────

/** GET /workflows/:id response. On success it carries only `workflow` (no
 * top-level `ok`, matching `workflow get`); on failure it carries `error`.
 * `ok: boolean` satisfies `apiRequest`'s generic constraint; the handlers
 * gate on `error`/`workflow`, never on `ok`. */
type WorkflowGetResponse = {
  ok: boolean;
  error?: string;
  workflow?: WorkflowDetailResponse['workflow'];
};

interface ForkResult {
  ok: boolean;
  error?: string;
  workflow?: {
    id: string;
    name: string | null;
    status?: string | null;
    forkedFromWorkflowId?: string | null;
  };
}

/** One mapped pair, fork node → origin node. */
export interface ForkNodeMapEntry {
  order: number;
  nodeType: string;
  forkNodeId: string;
  originalNodeId: string;
  /** Whether the configJson matched after expected-fork-delta normalization. */
  configMatched: boolean;
  /** Present when configJson did NOT match (warning surfaced in text mode). */
  configDiff?: string;
}

// ── Dispatcher ───────────────────────────────────────────────────────────

export async function handleWorkflowFork(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const [verb, ...rest] = argv;
  switch (verb) {
    case 'fork':
      await handleFork(rest, apiUrl, token);
      break;
    case 'fork-map':
      await handleForkMap(rest, apiUrl, token);
      break;
    case undefined:
      printHelp();
      break;
    default:
      console.error(`Unknown workflow fork sub-command: ${verb}`);
      printHelp();
      process.exit(1);
  }
}

function printHelp(): void {
  console.error(
    [
      'Usage: canary workflow fork <verb> [options]',
      '',
      'Verbs:',
      '  fork <workflowId>              Create an editable draft fork of a workflow',
      '  fork-map <forkId> <originalId> Map fork node ids to their origin counterparts',
      '',
      'Options:',
      '  --json      force JSON output',
      '  --text      force pretty-text output (default when stdout is a TTY)',
    ].join('\n')
  );
}

function wantsJson(argv: string[]): boolean {
  if (hasFlag(argv, '--json')) return true;
  if (hasFlag(argv, '--text')) return false;
  return !process.stdout.isTTY;
}

// ── `fork` handler ───────────────────────────────────────────────────────

async function handleFork(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  const json = wantsJson(argv);
  if (!workflowId) {
    console.error('Error: workflow fork requires a workflowId.');
    printHelp();
    process.exit(1);
  }

  const result = await apiRequest<ForkResult>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/fork`
  );
  if (!result.ok || !result.workflow) {
    if (json) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      console.error(`Error: ${result.error ?? 'fork failed'}`);
    }
    process.exit(1);
  }

  const fork = result.workflow;
  if (json) {
    console.log(JSON.stringify({ ok: true, workflow: fork }, null, 2));
    return;
  }

  console.log(`Forked "${fork.name ?? fork.id}" as draft ${fork.id}`);
  if (fork.forkedFromWorkflowId) {
    console.log(`  origin:     ${fork.forkedFromWorkflowId}`);
  }
  console.log(`  status:     ${fork.status ?? 'draft'}`);
  console.log(
    `  next:       canary workflow fork-map ${fork.id} ${workflowId}  # map nodes back to the origin`
  );
}

// ── `fork-map` handler ───────────────────────────────────────────────────

async function handleForkMap(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const [forkId, originalId] = positional;
  const json = wantsJson(argv);
  if (!forkId || !originalId) {
    console.error('Error: workflow fork-map requires <forkId> and <originalId>.');
    printHelp();
    process.exit(1);
  }

  const [forkRes, originalRes] = await Promise.all([
    apiRequest<WorkflowGetResponse>(apiUrl, token, 'GET', `/workflows/${forkId}`),
    apiRequest<WorkflowGetResponse>(apiUrl, token, 'GET', `/workflows/${originalId}`),
  ]);

  if (forkRes.error || !forkRes.workflow) {
    console.error(`Error: fork not found (${forkRes.error ?? forkId}).`);
    process.exit(1);
  }
  if (originalRes.error || !originalRes.workflow) {
    console.error(`Error: original not found (${originalRes.error ?? originalId}).`);
    process.exit(1);
  }

  const entries = buildForkNodeMap(forkRes.workflow, originalRes.workflow);

  if (json) {
    console.log(
      JSON.stringify(
        {
          ok: true,
          forkWorkflowId: forkId,
          originalWorkflowId: originalId,
          mapped: entries.length,
          nodes: entries,
        },
        null,
        2
      )
    );
    return;
  }

  if (entries.length === 0) {
    console.log('No nodes to map (both workflows have an empty node set).');
    return;
  }

  console.log(`Fork → original node mapping (${entries.length} nodes):`);
  for (const e of entries) {
    const match = e.configMatched ? 'ok' : 'DIFF';
    console.log(
      `  #${e.order}  ${e.nodeType.padEnd(16)} ${e.forkNodeId.slice(0, 8)} → ${e.originalNodeId.slice(0, 8)}  [${match}]`
    );
    if (e.configDiff) console.log(`         ${e.configDiff}`);
  }
  const diffs = entries.filter((e) => !e.configMatched);
  if (diffs.length > 0) {
    console.warn(
      `\nWarning: ${diffs.length} node(s) have config differences beyond the expected fork delta ` +
        `(apiSequenceId re-key). Playbooks for those nodes may not port cleanly to the origin.`
    );
  }
}

/**
 * Map fork nodes to origin nodes by edge-derived topological order.
 *
 * A fork copies the node graph + configJson faithfully with fresh node ids, so
 * topo order (edges, not deprecated orderIndex) positions correspond 1:1. The
 * one expected config delta is `apiSequenceId` on api_sequence nodes, which is
 * re-keyed to the fork's own row; it is normalized out of the comparison.
 */
export function buildForkNodeMap(
  forkWorkflow: LiveWorkflow,
  originalWorkflow: LiveWorkflow
): ForkNodeMapEntry[] {
  const forkOrdered = topoOrderNodes(forkWorkflow.nodes, forkWorkflow.edges ?? []);
  const originalOrdered = topoOrderNodes(originalWorkflow.nodes, originalWorkflow.edges ?? []);

  if (forkOrdered.length !== originalOrdered.length) {
    return [];
  }

  const entries: ForkNodeMapEntry[] = [];
  for (let i = 0; i < forkOrdered.length; i++) {
    const forkNode = forkOrdered[i]!;
    const originalNode = originalOrdered[i]!;
    const forkConfig = normalizeForMatch(forkNode.configJson);
    const originalConfig = normalizeForMatch(originalNode.configJson);
    const configMatched = deepEqual(forkConfig, originalConfig);
    entries.push({
      order: i + 1,
      nodeType: forkNode.nodeType,
      forkNodeId: forkNode.id,
      originalNodeId: originalNode.id,
      configMatched,
      ...(configMatched
        ? {}
        : { configDiff: `config differs from origin (node #${i + 1} ${forkNode.nodeType})` }),
    });
  }
  return entries;
}

/** Strip the fields that legitimately differ between a fork and its origin. */
function normalizeForMatch(
  configJson: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!configJson || typeof configJson !== 'object') return configJson;
  const copy = { ...configJson };
  delete copy.apiSequenceId;
  return copy;
}

/** Small deep-equality for plain JSON (configJson values). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a as Record<string, unknown>).sort();
    const bk = Object.keys(b as Record<string, unknown>).sort();
    if (ak.length !== bk.length) return false;
    return ak.every(
      (k, i) =>
        k === bk[i] &&
        deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[bk[i]!])
    );
  }
  return false;
}
